import NoData from '@/components/common/NoData'
import { countSubPipes } from './HydraulicTable/save-rules'
import { useGuidedEdit } from '@/features/config/contexts/guided-edit-context'
import SearchInput from '@/components/common/SearchInput'
import ButtonField from '@/components/form-control/ButtonField'
import { flyInVariantsDashboard } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { SelectChangeEvent, Stack } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useConfigUI } from '../../context'
import useSearchHydraulicInfinityList from '../../hooks/hydraulic/useSearchHydraulicInfinityList'
import HydraulicTable, { HydraulicTableRef } from './HydraulicTable'
import classes from './style.module.scss'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import { useDataConfigTab } from '../DataConfiguration/DataConfigTabContext'

const MotionDiv = motion.div

// `interfaceId` is optional so the grid can be embedded (e.g. guided setup,
// SCRUM-1032) scoped to an explicit controller. When omitted, the normal
// /config screen behaviour is unchanged: it falls back to the selected
// interface from auth.
interface HydraulicProps {
  interfaceId?: number | string
  embedded?: boolean
}

const Hydraulic = ({
  interfaceId: propInterfaceId,
  embedded = false,
}: HydraulicProps = {}) => {
  const { t } = useTranslation()

  const { openModal, setModalView } = useConfigUI()

  const { interfaceId: authInterfaceId } = useAuthContext()
  const interfaceId = propInterfaceId ?? authInterfaceId
  const { isViewOnly } = usePermission()
  // `currentTab` is null outside DataConfigTabProvider, so this is true only
  // when the page is rendered as one of that page's tabs.
  const isEmbedded = embedded || useDataConfigTab().currentTab !== null
  const tableRef = useRef<HydraulicTableRef>(null)
  // SCRUM-1032: inside the guided setup the grid opens in bulk edit and its
  // pending rows are flushed when the wizard moves on.
  const bulkEditByDefault = useGuidedEdit('hydraulic', () =>
    tableRef.current?.handleSave(),
  )
  const [bulkEditMode, setBulkEditMode] = useState(bulkEditByDefault)
  const [keyword, setKeyword] = useState<string | undefined>(undefined)

  // Search runs on the server, so it matches every pipe rather than only the
  // pages already fetched.
  const handleSearch = useMemo(
    () =>
      debounce((event: SelectChangeEvent) => {
        const value = event.target.value.toString().trim()
        setKeyword(value || undefined)
      }, 500),
    [],
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useSearchHydraulicInfinityList({
      interfaceId: Number(interfaceId),
      keyword,
    })

  const rows = useMemo(() => {
    return data?.pages.flatMap((page) => page.list) ?? []
  }, [data])

  const totalCount = data?.pages[0]?.totalCount ?? 0

  // Stable, because the table's save callback depends on it and the guided
  // autosave restarts its timer whenever that callback changes.
  const leaveBulkEdit = useCallback(() => setBulkEditMode(false), [])

  const onBodyScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // A short viewport never fires onBodyScrollEnd, so keep pulling pages until
  // the rows overflow. The table answers this rather than the page reaching
  // into its DOM.
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || rows.length === 0) return
    const timeoutId = setTimeout(() => {
      if (tableRef.current?.hasRoomForMoreRows()) fetchNextPage()
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [rows.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleDelete = (
    id: number,
    interfaceId: string,
    flowRuleId?: string,
    name?: string,
  ) => {
    // ViewOnly users cannot delete pipe
    if (isViewOnly) return

    openModal()
    setModalView({
      type: 'DELETE_HYDRAULIC_VIEW',
      id: id.toString(),
      interfaceId: interfaceId,
      name,
      flowRuleId,
      subPipeCount: countSubPipes(id, rows as any[]),
      onSuccess: async () => {
        // A pending edit on the deleted pipe would otherwise hold the step
        // with no cell left to fix (round 7 review, H2).
        tableRef.current?.forgetPipe(Number(id))
        await refetch()
      },
    })
  }

  const body = (
    <Stack className={classes.wrapper}>
      <Stack flexGrow="1" height="100%">
        <Stack className={classes.header}>
          <MotionDiv
            initial="initialRight"
            animate="animate2"
            variants={flyInVariantsDashboard}
            style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0, overflow: 'hidden' }}
            >
              {/* SCRUM-1032: no search in the wizard — a filtered pipe tree
                  hides the branches the user is still building. */}
              {!bulkEditByDefault && (
                <SearchInput
                  name={t('commonActionSearchHydraulic')}
                  onChange={handleSearch}
                />
              )}
            </Stack>
          </MotionDiv>

          <MotionDiv
            initial="initialLeft"
            animate="animate2"
            variants={flyInVariantsDashboard}
            style={{ flexShrink: 0 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {/* SCRUM-1032: the guided wizard's Next flushes every grid, so
                  its per-grid Cancel / Save (and the Edit toggle) are hidden. */}
              {!bulkEditByDefault &&
                (bulkEditMode ? (
                  <>
                    <ButtonField
                      startIcon={<CancelIcon />}
                      style={{
                        height: '32px',
                        minWidth: '70px',
                        borderRadius: '5px',
                        backgroundColor: '#fff2f2',
                        color: '#dc3545',
                        border: '1px solid #f3c2c5',
                      }}
                      name={t('commonActionsCancel')}
                      onClick={() => {
                        tableRef.current?.handleCancel()
                        setBulkEditMode(false)
                      }}
                    />
                    <ButtonField
                      startIcon={<SaveIcon />}
                      style={{
                        height: '32px',
                        minWidth: '70px',
                        borderRadius: '5px',
                        backgroundColor: '#004744',
                        color: '#fff',
                      }}
                      name={t('commonActionsSave')}
                      onClick={() => tableRef.current?.handleSave()}
                    />
                  </>
                ) : (
                  <ButtonField
                    startIcon={<EditIcon />}
                    style={{
                      height: '32px',
                      minWidth: '89px',
                      borderRadius: '5px',
                      opacity: isViewOnly ? 0.6 : 1,
                    }}
                    name={t('commonActionsBulkEdit')}
                    onClick={() => !isViewOnly && setBulkEditMode(true)}
                    disabled={isViewOnly}
                  />
                ))}
            </Stack>
          </MotionDiv>
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          {/* Mounted while empty in two cases: bulk edit, because the "Add
              New Mainline" create row is pinned to the grid and unmounting
              would remove the only way to add the first pipe; and the first
              load, so the grid can draw its own skeleton rows. */}
          {(!data || rows.length > 0 || bulkEditMode) && (
            <HydraulicTable
              ref={tableRef}
              interfaceId={interfaceId ?? undefined}
              rows={rows}
              loading={!data}
              onDelete={handleDelete}
              bulkEditMode={bulkEditMode}
              onToggleBulkEdit={leaveBulkEdit}
              onRefetch={refetch}
              onBodyScrollEnd={onBodyScrollEnd}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}

          {data && totalCount === 0 && !bulkEditMode && (
            <NoData text={t('noData')} />
          )}
        </MotionDiv>
      </Stack>
    </Stack>
  )

  // Embedded as a Data Configuration tab: that page already supplies the rail
  // offset and the title bar, so a second ConfigLayout here would nest one
  // inside the other and draw the header twice.
  if (isEmbedded) return body

  // Standalone route. ConfigLayout is what holds the page clear of the fixed
  // 76px menu rail and gives it the same title bar as every other config page;
  // without it the page drew from x=0 and the rail covered the Pipe Name
  // column.
  return (
    <ConfigLayout
      title={t('commonWordHydraulic')}
      header={
        <ConfigHeader
          subtitle={t('dataConfig')}
          title={t('commonWordHydraulic')}
        />
      }
    >
      {body}
    </ConfigLayout>
  )
}

export default Hydraulic
